<template>
  <nav :class="{'side-panel_opened': opened}" class="side-panel">
    <icon-base
      white
      @click.native="toggleSidePanel(!opened)"
      class="side-panel__switcher side-panel__switcher_menu"
    >
      <icon-menu></icon-menu>
    </icon-base>
    <router-link
      to="/admin"
      v-if="userIsAdmin"
      title="Admin Panel"
      class="side-panel__link side-panel__link_admin"
    >
      <span>Admin Panel</span>
      <span>A</span>
    </router-link>
    <template v-if="loginInfo">
      <router-link
        to="/dashboard"
        title="Dashboard"
        class="side-panel__link side-panel__link_possible-notification"
        :class="{ 'side-panel__link_has-notification': dashboardHasNotifications }"
      >
        <span>Dashboard</span>
        <icon-dashboard></icon-dashboard>
      </router-link>
    </template>
    <router-link
      to="/all-currencies"
      title="All currencies"
      class="side-panel__link side-panel__link_possible-notification"
      :class="{ 'side-panel__link_has-notification': allCurrenciesHasNotifications }"
    >
      <span>All currencies</span>
      <icon-currencies></icon-currencies>
    </router-link>
    <template v-if="loginInfo">
      <div class="separator"/>
      <router-link
        to="/subscription"
        title="Subscription"
        class="side-panel__link side-panel__link_possible-notification"
        :class="{ 'side-panel__link_has-notification': subscriptionHasNotifications }"
      >
        <span>Subscriptions</span>
        <icon-subscription></icon-subscription>
      </router-link>
      <router-link
        to="/settings"
        title="Settings"
        class="side-panel__link side-panel__link_possible-notification"
        :class="{ 'side-panel__link_has-notification': settingsHasNotifications }"
      >
        <span>Settings</span>
        <icon-settings></icon-settings>
      </router-link>
      <div
        title="Logout"
        @click="logOut"
        class="side-panel__link"
      >
        <span>Logout</span>
        <icon-base white>
          <icon-logout/>
        </icon-base>
      </div>
    </template>
    <template v-else>
      <router-link
        to="/authorization-page/sign-in"
        title="Login"
        class="side-panel__link side-panel__link_sign-in"
      >
        <span>Login</span>
        <icon-base white>
          <icon-arrow-square-btn/>
        </icon-base>
      </router-link>
      <router-link
        to="/authorization-page/sign-up"
        title="Sign up"
        class="side-panel__link side-panel__link_sign-up"
      >
        <span>Sign up</span>
        <icon-base
          white
          class="icon-register"
        >
          <icon-circle-plus/>
        </icon-base>
      </router-link>
    </template>
    <div class="support-link">
      <div class="support-link__title">
        Have a problem?
      </div>
      <router-link
        title="Support"
        to="/support"
        class="side-panel__link side-panel__link_possible-notification support-link__router-link"
        :class="{ 'side-panel__link_has-notification': supportHasNotifications }"
      >
        <span>Support</span>
        <icon-support class="icon-support"></icon-support>
      </router-link>
    </div>
    <the-revision v-if="!envIsProduction" class="revision" />
  </nav>
</template>

<script>
import sidePanelState from 'src/models/SidePanelState';
import { mapActions, mapState, mapGetters } from 'vuex';
import {
  envIsProduction,
  sidePanelStateLocalStorageName,
} from 'src/utils/config';
import {
    IconMenu,
    IconLogout,
    IconSupport,
    IconSettings,
    IconArrowBtn,
    IconDashboard,
    IconCurrencies,
    IconCirclePlus,
    IconSubscription,
    IconArrowSquareBtn,
} from 'Icons';
import TheRevision from './TheRevision';

export default {
  data: () => ({
    envIsProduction,
  }),
  components: {
    IconMenu,
    IconLogout,
    IconSupport,
    TheRevision,
    IconSettings,
    IconArrowBtn,
    IconDashboard,
    IconCurrencies,
    IconCirclePlus,
    IconSubscription,
    IconArrowSquareBtn,
  },
  computed: {
    ...mapGetters('userData', ['userIsAdmin']),
    ...mapState('userData', ['loginInfo']),
    ...mapState('userNotifications', ['userNotifications']),
    ...mapState('sidePanel', ['opened']),
    dashboardHasNotifications() {
      return this.userNotifications.some(n => n.notificationKey.startsWith('dashboard:'));
    },
    allCurrenciesHasNotifications() {
      return false; // TODO implement ?
    },
    supportHasNotifications() {
      return false; // TODO implement ?
    },
    settingsHasNotifications() {
      return this.userNotifications.some(n => n.notificationKey.startsWith('settings:'));
    },
    subscriptionHasNotifications() {
      return this.userNotifications.some(n => n.notificationKey.startsWith('subscriptions:'));
    },
  },
  methods: {
    ...mapActions('userData', ['logOut']),
    ...mapActions('sidePanel', ['toggleSidePanel']),
  },
  mounted() {
    if (localStorage[sidePanelStateLocalStorageName] === sidePanelState.closed) {
      this.toggleSidePanel(false);
    }
  },
};
</script>

<style lang="scss" scoped>
  .side-panel {
    z-index: 9999;
    $padding-right: 22px;
    $opened-width: 210px;
    padding: 126px $padding-right 0 24px;
    height: 100vh;
    position: fixed;
    left: -($opened-width - $closed-sidepanel-width);
    top: 0;
    width: $opened-width;
    background: $black-color3;
    transition: $transition/2 left ease-out;
    user-select: none;
    min-height: 550px;
    &_opened {
      @include incrSelectorWeight(1) {
        left: 0;
      }
      .support-link {
        transform: translate3d(0, 0, 0);
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        width: 100%;
        left: 0;
        &__title,
        span {
          opacity: 1 !important;
        }
      .icon-support {
        position: static;
        left: auto;
        top: auto;
      }
        &__router-link {
          width: 80px !important;
          height: 68px !important;
          margin-left: -5px;
          &:after {
            display: none;
          }
          &:hover {
            background: $grey-color14;
          }
          .icon-support {
            top: 26px;
          }
        }
      }
      .side-panel__switcher {
        right: 172px;
      }
      .revision {
        display: none;
      }
      &#{&} .separator {
        width: 100%;
        transform: translateX(0);
      }
    }
    &__switcher {
      cursor: pointer;
      right: $padding-right;
      top: 24px;
      transition: $transition/2 right ease-out;
      position: absolute;
    }
    &__link {
      display: flex;
      justify-content: space-between;
      @include font(13px, $white-color2, 500);
      text-decoration: none;
      height: 20px;
      cursor: pointer;
      outline: none;
      &_admin {
        margin-right: 3px;
      }
      &_possible-notification {
        margin-right: -5px;
      }
      &_sign-in,
      &_sign-up {
        display: none;
      }
      &:not(:last-child) {
        margin-bottom: 40px;
      }
      &:hover {
        span {
          color: $red-color6;
        }
      }
    }
    .separator {
      transition: $transition/2;
      width: 15px;
      transform: translateX(149px);
      margin-bottom: 43px;
      height: 1px;
      background: $white-color2;
      opacity: 0.3;
    }
  }
  .support-link {
    position: absolute;
    bottom: 17px;
    transform: translate3d(74px, 0, 0);
    transition: $transition/2;
    will-change: transform;
    &__title {
      @include font(10px, $grey-color2, 500);
      text-align: center;
      opacity: 0;
      margin-bottom: 16px;
    }
    &__router-link {
      display: flex;
      flex-direction: column-reverse;
      justify-content: space-between;
      padding: 14px 0;
      align-items: center;
      border-radius: 10px;
      transition: background $transition;
      width: 71px;
      height: 0;
      span {
        color: $white-color2;
        height: 16px;
        opacity: 0;
        transition: opacity $transition/2;
      }
      .icon-support {
        position: absolute;
        left: 75px;
        top: 2px;
      }
      &:after {
        content: '';
        opacity: 0;
        width: 32px;
        height: 32px;
        transition: opacity $transition;
        background: $grey-color14;
        position: absolute;
        top: -7px;
        left: 66px;
        border-radius: 10px;
      }
      &:hover {
        &:after {
          opacity: 1;
        }
      }
    }
    &:hover {
      span {
        color: $white-color2;
      }
    }
  }
  .revision {
    position: absolute;
    bottom: 17px;
    right: 15px;
    .side-panel_opened & {
      bottom: 1px;
      right: 0;
      left: 0;
    }
  }
  .icon-register {
    margin-top: -2px;
  }
  @include mobileStyles {
    .side-panel {
      left: -210px;
      padding: 71px 22px 0 24px;
      min-height: 480px;
    }
    .side-panel__switcher {
      &_menu {
        display: none;
      }
    }
    .side-panel__link {
      &_sign-in,
      &_sign-up {
        display: flex;
      }
    }
  }
</style>




// WEBPACK FOOTER //
// src/components/layout/SidePanel/index.vue